import {
  SchemaCancelOrderResultDto,
  SchemaDryRunOrderCreatedDto,
  SchemaDryRunOrderFailedDto,
  SchemaSubmitOrderCreatedDto,
  SchemaSubmitOrderFailedDto,
} from '../generated/apiTypes';
import { DryRunResult, unexpectedErrorMessage } from '../hooks/useDryRunWs';
import { captureError } from './errorTracking';
import { formatDollar } from './formatters';
import { Gwei } from './gwei';

export const ORDER_SUCCESS_RESULT = 'Ok' satisfies SchemaSubmitOrderCreatedDto['result'];

type SubmitOrderCreatedError = Exclude<
  NonNullable<SchemaSubmitOrderCreatedDto['result']>,
  typeof ORDER_SUCCESS_RESULT
>;
type SubmitOrderFailedError = Exclude<
  NonNullable<SchemaSubmitOrderFailedDto['message']>,
  typeof ORDER_SUCCESS_RESULT
>;
type SubmitOrderCreatedErrorMessageFn =
  | string
  | ((orderSubmitResult: SchemaSubmitOrderCreatedDto | undefined) => string);

type CustomErrorCode = 'SignerExpired';

type ErrorCodes = SubmitOrderCreatedError | SubmitOrderFailedError | CustomErrorCode;
const orderSubmitErrorMessages: Record<ErrorCodes, SubmitOrderCreatedErrorMessageFn> = {
  // SubmitOrderFailedError
  ExchangeSuspended: 'Trading is currently suspended on this exchange. Please try again later.',
  TooManyOpenOrders: 'You have reached the maximum number of open orders allowed.',
  TooManyStopOrders: 'You have reached the maximum number of stop orders allowed.',
  TooManyPositions: 'Too many positions open.',

  // SubmitOrderCreatedError
  OpenValueCapExceeded:
    'Open interest cap has been exceeded for this instrument. Please reduce your order size.',
  TriggerCanceledError: 'Order will be rejected as the trigger condition for it has been canceled.',
  LiquidationError: 'Order was rejected as it may have triggered liquidation.',
  ImmediateMatchPostOnly: 'Post-only order would have been matched immediately.',
  RiskLimitExceeded: 'Order exceeded the current risk limit.',
  OrderIncreasesPosition:
    'Order would have increased your position. Turn off reduce only or lower the amount.',
  UnfilledImmediateOrCancel: 'IOC order could not be fully filled immediately.',
  UnfilledFillOrKill: 'FOK order could not be filled in its entirety immediately.',
  InsufficientBalance: 'Insufficient balance for order.',
  UnfilledMarketOrder: 'Order was rejected as it could not be fully filled.',
  OcoFilled: 'The OCO order counterpart was filled or canceled.',
  MarketOrderReachedMaxSlippage: (data) =>
    data && Gwei.toGwei(data.filled) !== 0n
      ? `Order hit maximum slippage after being partially filled`
      : 'Order hit maximum slippage',
  CausesImmediateLiquidation: 'Order was rejected as it would cause immediate liquidation.',

  // Custom codes
  // We should prevent this, but if it happens due to signer expiring while the app is open, we want to show a user-friendly message
  SignerExpired: 'Signer has expired. Please re-enable trading.',
};

const addCustomErrorCode = (
  code: SubmitOrderCreatedError | SubmitOrderFailedError | undefined
): ErrorCodes | undefined => {
  if (code?.startsWith('Signer expired')) {
    return 'SignerExpired';
  }
  return code;
};

export const getOrderSubmitErrorMessage = (
  code: SubmitOrderCreatedError | SubmitOrderFailedError | undefined,
  data?: SchemaSubmitOrderCreatedDto | undefined
): string => {
  const codeWithCustom = addCustomErrorCode(code);
  const fnOrString = codeWithCustom && orderSubmitErrorMessages[codeWithCustom];
  if (typeof fnOrString === 'function') {
    return fnOrString(data);
  }

  return fnOrString ?? `Order was rejected due to an unknown issue (${codeWithCustom})`;
};

type DryRunRejectReason = Exclude<
  NonNullable<SchemaDryRunOrderFailedDto['code'] | SchemaDryRunOrderCreatedDto['code']>,
  typeof ORDER_SUCCESS_RESULT
>;
type DryRunRejectMessageFn = (dryRunDto: DryRunResult) => string;

const dryRunErrorMessages: Record<DryRunRejectReason, DryRunRejectMessageFn> = {
  ImmediateMatchPostOnly: () => 'Post-only order will be matched immediately.',
  RiskLimitExceeded: (dto) =>
    `Order exceeds the current risk limit. Risk used: ${formatDollar(dto.riskUsed)}, risk available: ${formatDollar(dto.riskAvailable)}.`,
  OrderIncreasesPosition: () =>
    'Order will increase your position. Turn off reduce only or lower the amount.',
  UnfilledImmediateOrCancel: () => 'IOC order cannot be fully filled immediately.',
  UnfilledFillOrKill: () => 'FOK order cannot be filled in its entirety immediately.',
  InsufficientBalance: (dto) =>
    `Insufficient balance for order. Required: ${formatDollar(dto.marginRequired)}, available: ${formatDollar(dto.marginAvailable)}.`,
  LiquidationError: () => 'Order will be rejected as it may trigger liquidation.',
  UnfilledMarketOrder: () => 'Order will be rejected as it cannot be fully filled.',
  OpenValueCapExceeded: () => 'Open interest cap will be exceeded.',
  TriggerCanceledError: () =>
    'Order will be rejected as the trigger condition for it has been canceled.',
  OcoFilled: () => 'The OCO order counterpart has been filled or canceled.',
  MarketOrderReachedMaxSlippage: () => 'Order will hit maximum slippage.',
  CausesImmediateLiquidation: () =>
    'Order will be rejected as it would cause immediate liquidation.',
  TooManyOpenOrders: () => 'You have too many open orders.',
  TooManyStopOrders: () => 'You have too many stop orders.',
  ExchangeSuspended: () => 'The exchange is currently suspended.',
  TooManyPositions: () => 'You have too many open positions.',
};

export function getDryRunErrorMessage(dryRunDto: DryRunResult) {
  const { code } = dryRunDto;
  if (!code || code === ORDER_SUCCESS_RESULT) return undefined;

  const fn = dryRunErrorMessages[code];
  if (!fn) {
    captureError(new Error(`Unknown dry run error code: ${code}`));
    return unexpectedErrorMessage;
  }
  return fn(dryRunDto);
}

export type CancelOrderError = Exclude<
  NonNullable<SchemaCancelOrderResultDto['result']>,
  typeof ORDER_SUCCESS_RESULT
>;

const orderCancelErrorMessages: Record<CancelOrderError, string> = {
  AlreadyCanceled: 'Order has already been canceled.',
  AlreadyExpired: 'Order was already expired.',
  AlreadyFilled: 'Order has already been filled.',
  NotFound: 'Order was not found.',
};

export const getOrderCancelErrorMessage = (
  code: CancelOrderError | undefined
): string | undefined => (code && orderCancelErrorMessages[code]) || code;
